import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCustomers, selectCustomers } from '../../customer/customerSlice';
import { fetchSites, selectSites } from '../../site/siteSlice';
import { customerService } from '../../customer/customerService';
import { siteService } from '../../site/siteService';
import UnifiedOrderPage from './UnifiedOrderPage';

// Props for CreateOrderPage
interface CreateOrderPageProps {
  preSelectedCustomer?: any;
  preSelectedSite?: any;
  onOrderCreated?: (order: any) => void;
  onClose?: () => void;
}

// Main Create Order Page Component
const CreateOrderPage: React.FC<CreateOrderPageProps> = ({ 
  preSelectedCustomer, 
  preSelectedSite, 
  onOrderCreated,
  onClose 
}) => {
  const { customerId, siteId } = useParams<{ customerId?: string; siteId?: string }>();
  const dispatch = useDispatch();
  
  // Redux state
  const customers = useSelector(selectCustomers);
  const sites = useSelector(selectSites);
  
  // Local state for resolved customer and site
  const [resolvedCustomer, setResolvedCustomer] = useState<any>(null);
  const [resolvedSite, setResolvedSite] = useState<any>(null);
  const [resolvedSites, setResolvedSites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Load data based on URL parameters
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        if (customerId && customerId !== "undefined" && customerId !== "null") {
          // Fetch customer and sites directly from API
          console.log('🔄 Fetching customer and sites for customerId:', customerId);
          
          const [customerResponse, sitesResponse] = await Promise.all([
            customerService.getCustomer(customerId),
            siteService.getSites({ customerId })
          ]);
          
          if (customerResponse.success) {
            console.log('✅ Customer fetched successfully:', customerResponse.data);
            // The customer data might be nested under response.data.customer or directly in response.data
            const rawCustomer = (customerResponse.data as any).customer || customerResponse.data;
            console.log('✅ Raw customer data:', rawCustomer);
            
            // Transform customer data to match the expected interface
            const customer = {
              id: rawCustomer.id,
              name: rawCustomer.name,
              code: rawCustomer.code || `C${rawCustomer.id}`,
              email: rawCustomer.email,
              phone: rawCustomer.phone,
              address: rawCustomer.address || "",
              city: rawCustomer.city || "",
              state: rawCustomer.state || "",
              zipCode: rawCustomer.postal_code || rawCustomer.zipCode || "",
              contactPerson: rawCustomer.contact_person || rawCustomer.contactPerson || "",
              contactPhone: rawCustomer.contact_phone || rawCustomer.contactPhone || "",
              contactEmail: rawCustomer.contact_email || rawCustomer.contactEmail || "",
              status: rawCustomer.is_active !== undefined ? rawCustomer.is_active : (rawCustomer.status !== undefined ? rawCustomer.status : true),
              branchId: rawCustomer.branch_id || rawCustomer.branchId || "",
              type: rawCustomer.type || "individual",
              createdAt: rawCustomer.created_at || rawCustomer.createdAt,
              updatedAt: rawCustomer.updated_at || rawCustomer.updatedAt,
            };
            
            console.log('✅ Transformed customer:', customer);
            setResolvedCustomer(customer);
          } else {
            console.error('❌ Failed to fetch customer:', customerResponse.error);
            setError('Failed to load customer data');
          }
          
          if (sitesResponse.success) {
            console.log('✅ Sites fetched successfully:', sitesResponse.data);
            // The sites data is nested under response.data.sites
            const sites = sitesResponse.data.sites || [];
            console.log('✅ Extracted sites:', sites);
            console.log('🔍 Raw site object structure:', sites[0]); // Log first site to see structure
            
            // Transform sites data to match the expected interface
            const transformedSites = sites.map((site: any) => ({
              id: site.id,
              name: site.name,
              address: site.address || "",
              city: site.city || "",
              state: site.state || "",
              zipCode: site.zip_code || site.zipCode || "",
              contactPerson: site.contact_person || site.contactPerson || "",
              contactPhone: site.contact_phone || site.contactPhone || "",
              status: site.status || "active",
              notes: site.notes || "",
              customerId: site.customer_id || site.customerId,
              createdAt: site.created_at || site.createdAt,
              updatedAt: site.updated_at || site.updatedAt,
              totalOrder: site.total_order || site.totalOrder || 0,
              completedOrder: site.completed_order || site.completedOrder || 0,
              pendingOrder: site.pending_order || site.pendingOrder || 0,
            }));
            
            console.log('✅ Transformed sites:', transformedSites);
            console.log('🔍 Transformed site customerId:', transformedSites[0]?.customerId);
            console.log('🔍 Raw site customer_id:', sites[0]?.customer_id);
            
            // Store sites locally for immediate use
            setResolvedSites(transformedSites);
            
            // Also store sites in Redux for the UnifiedOrderPage to use
            dispatch(fetchSites({ customerId }) as any);
          } else {
            console.error('❌ Failed to fetch sites:', sitesResponse.error);
            setError('Failed to load sites data');
          }
        } else if (customerId === "undefined" || customerId === "null") {
          // Invalid customerId, show error
          console.error('❌ Invalid customerId provided:', customerId);
          setError('Invalid customer ID. Please go back and try again.');
        } else {
          // No customerId, load all customers and sites only if not already loaded
          console.log('🔄 No customerId provided, loading all customers and sites');
          if (customers.length === 0) {
            dispatch(fetchCustomers({}) as any);
          }
          if (sites.length === 0) {
            dispatch(fetchSites({}) as any);
          }
        }
      } catch (error) {
        console.error('❌ Error loading data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [dispatch, customerId, customers.length, sites.length]);
  
  // Handle siteId if provided in URL
  useEffect(() => {
    if (siteId && sites.length > 0) {
      console.log('🔍 Looking for site with ID:', siteId);
      console.log('🔍 Available sites:', sites.map(s => ({ id: s.id, name: s.name, customerId: s.customerId })));
      const site = sites.find(s => s.id === siteId);
      if (site) {
        console.log('✅ Resolved site from URL:', site);
        setResolvedSite(site);
      } else {
        console.log('❌ Site not found with ID:', siteId);
      }
    }
  }, [siteId, sites]);
  
  // Use resolved data or props
  const finalCustomer = resolvedCustomer || preSelectedCustomer;
  const finalSite = resolvedSite || preSelectedSite;
  
  console.log('🔄 CreateOrderPage resolved data:', {
    customerId,
    siteId,
    resolvedCustomer: !!resolvedCustomer,
    resolvedCustomerData: resolvedCustomer,
    resolvedSite: !!resolvedSite,
    resolvedSiteData: resolvedSite,
    preSelectedCustomer: !!preSelectedCustomer,
    preSelectedSite: !!preSelectedSite,
    finalCustomer: !!finalCustomer,
    finalCustomerData: finalCustomer,
    finalSite: !!finalSite,
    finalSiteData: finalSite,
    loading,
    error
  });
  
  // Show loading state
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading customer and site data...</div>
      </div>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        gap: '16px'
      }}>
        <div style={{ color: 'red' }}>Error: {error}</div>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }
  
  console.log('🔍 CreateOrderPage passing to UnifiedOrderPage:', {
    finalCustomer: finalCustomer,
    finalSite: finalSite,
    resolvedSites: resolvedSites,
    resolvedSitesLength: resolvedSites?.length
  });

  return (
    <UnifiedOrderPage 
      mode="create"
      preSelectedCustomer={finalCustomer}
      preSelectedSite={finalSite}
      preSelectedSites={resolvedSites}
      onOrderCreated={onOrderCreated}
      onClose={onClose}
    />
  );
};

export default CreateOrderPage;